import { z } from 'zod';
import { CompassTransformScript } from '@scale/genai-compass-shared/cards/transform_script';

// Base schema for all script parameters
export const BaseScriptParamsSchema = z.object({
  script: z.nativeEnum(CompassTransformScript),
});

export type BaseScriptParams = z.infer<typeof BaseScriptParamsSchema>;

// Distribution type for percentage-based distributions
export const DistributionSchema = z.record(z.string(), z.number());
export type Distribution = z.infer<typeof DistributionSchema>;

// OTS UI Prompt Generation specific parameters
export const OTSUIPromptGenerationParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.OTS_UI_PROMPT_GENERATION),
  languageDistribution: DistributionSchema.default({
    'HTML/JS/CSS': 0.25,
    'React/TypeScript': 0.25,
    'Next.js': 0.25,
    Svelte: 0.09,
    Vue: 0.1,
    'Solid.js': 0.06,
  }),
  layoutDistribution: DistributionSchema.default({
    Desktop: 0.5,
    Mobile: 0.3,
    Both: 0.2,
  }),
  categoryDistribution: DistributionSchema.default({
    'Content Platforms': 0.088,
    'Business/E-commerce': 0.07,
    Games: 0.088,
    Simulations: 0.088,
    'Productivity & Development Tools': 0.132,
    'Interactive Data & Visualization Tools': 0.132,
    'Creative & Visual Design Solutions': 0.088,
    'Social Media & Communication': 0.088,
    'Learning & Education Interfaces': 0.044,
    'Clones of Existing Applications': 0.044,
    Other: 0.138,
  }),
  similarityThreshold: DistributionSchema.default({
    'Similarity Threshold': 0.85,
  }),
});

export type OTSUIPromptGenerationParams = z.infer<typeof OTSUIPromptGenerationParamsSchema>;

// CDS Download Response specific parameters
export const CDSDownloadResponseParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.CDS_DOWNLOAD_RESPONSE),
  jsonata: z.string().default('(\n$\n)'),
});

export type CDSDownloadResponseParams = z.infer<typeof CDSDownloadResponseParamsSchema>;

// Default column mapping for CDS Content Insertion
export const DEFAULT_CDS_CONTENT_INSERTION_COLUMN_MAPPING: Record<string, string> = {
  '': '',
};

// CDS Content Insertion specific parameters
export const CDSContentInsertionParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.CDS_CONTENT_INSERTION),
  columnMapping: z
    .record(z.string(), z.string())
    .default(DEFAULT_CDS_CONTENT_INSERTION_COLUMN_MAPPING),
});

export type CDSContentInsertionParams = z.infer<typeof CDSContentInsertionParamsSchema>;

// Default column mapping for CDS URL Signing
export const DEFAULT_CDS_URL_SIGNING_COLUMN_MAPPING: Record<string, string> = {
  '': '',
};

// CDS URL Signing specific parameters
export const CDSUrlSigningParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.CDS_URL_SIGNING),
  columnMapping: z.record(z.string(), z.string()).default(DEFAULT_CDS_URL_SIGNING_COLUMN_MAPPING),
  ttlSeconds: z.number().default(7 * 24 * 60 * 60), // Default: 7 days
});

export type CDSUrlSigningParams = z.infer<typeof CDSUrlSigningParamsSchema>;

// Default column mapping for S3 URL Signing
export const DEFAULT_S3_URL_SIGNING_COLUMN_MAPPING: Record<string, string> = {
  '': '',
};

// S3 URL Signing specific parameters
export const S3UrlSigningParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.S3_URL_SIGNING),
  columnMapping: z.record(z.string(), z.string()).default(DEFAULT_S3_URL_SIGNING_COLUMN_MAPPING),
  ttlSeconds: z.number().default(7 * 24 * 60 * 60), // Default: 7 days
});

export type S3UrlSigningParams = z.infer<typeof S3UrlSigningParamsSchema>;

// Hubstaff Screenshot specific parameters
export const HubstaffScreenshotParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.HUBSTAFF_SCREENSHOT),
  project_id: z.string().optional().default('3686763'),
  hours_back: z.number().optional().default(24),
  end_timestamp: z
    .string()
    .optional()
    .refine(
      val => {
        if (!val || val.trim() === '') return true; // Empty is valid
        // Validate ISO 8601 format
        const date = new Date(val);
        return !isNaN(date.getTime()) && date.toISOString() === val;
      },
      {
        message: 'Must be a valid ISO 8601 timestamp (e.g., 2025-01-15T10:30:00.000Z)',
      },
    ),
});

export type HubstaffScreenshotParams = z.infer<typeof HubstaffScreenshotParamsSchema>;

// MultiMango QA Table specific parameters
export const MultiMangoQATableParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.MULTIMANGO_QA_TABLE),
  email: z.string(),
  password: z.string(),
  dateFilter: z.enum(['all', '24h']).optional().default('all'),
  expandTaskColumns: z.boolean().optional().default(true),
});

export type MultiMangoQATableParams = z.infer<typeof MultiMangoQATableParamsSchema>;

export const DEFAULT_MULTIMANGO_PASSWORD_RESET_COLUMN_MAPPING: Record<string, string> = {
  '': '',
};

export const MultimangoPasswordResetParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.MULTIMANGO_USER_PASSWORD_RESET),
  emailColumn: z.string().default(''), // Source column containing contributor emails
  newPassword: z.string().default(''),
  outputColumn: z.string().default('password_reset_status'), // Destination column for status
});

export type MultimangoPasswordResetParams = z.infer<typeof MultimangoPasswordResetParamsSchema>;

// Set CB Role specific parameters
export const SetCBRoleParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.SET_CB_ROLE),
  emailColumn: z.string().default(''), // Source column containing contributor emails
  role: z.enum(['Unapproved', 'Unknown', 'Approved']).default('Unknown'),
  outputColumn: z.string().default('cb_role_status'), // Destination column for status
});

export type SetCBRoleParams = z.infer<typeof SetCBRoleParamsSchema>;

// Sync Multimango Users specific parameters
export const SyncMultimangoUsersParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.SYNC_MULTIMANGO_USERS),
  userIdColumn: z.string().default(''), // Source column for userId
  userPersonalEmailColumn: z.string().default(''), // Source column for userPersonalEmail
  userWfeEmailColumn: z.string().default(''), // Source column for userWfeEmail
  wfePasswordColumn: z.string().default(''), // Source column for wfePassword
  outputColumn: z.string().default('sync_status'), // Destination column for status
});

export type SyncMultimangoUsersParams = z.infer<typeof SyncMultimangoUsersParamsSchema>;

// WFE Decrypt Passwords specific parameters
export const WfeDecryptPasswordsParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.WFE_DECRYPT_PASSWORDS),
  passwordColumn: z.string().default(''), // Source column containing encrypted passwords
  outputColumn: z.string().default('password_decrypted'), // Destination column for decrypted password
});

export type WfeDecryptPasswordsParams = z.infer<typeof WfeDecryptPasswordsParamsSchema>;

// Add Hubstaff Hours specific parameters
export const AddHubstaffHoursParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.ADD_HUBSTAFF_HOURS),
});

export type AddHubstaffHoursParams = z.infer<typeof AddHubstaffHoursParamsSchema>;

// Agent Execution specific parameters
export const AgentExecutionParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.AGENT_EXECUTION),
  model: z.string().default('openai/gpt-5'),
});

export type AgentExecutionParams = z.infer<typeof AgentExecutionParamsSchema>;

// FNA1 Hard Prompts Rubrics Bot Attempt specific parameters
export const FNA1HardPromptsRubricsParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.FNA1_HARD_PROMPTS_RUBRICS_BOT_ATTEMPT),
  DRY_RUN_MODE: z.boolean().default(true),
});

export type FNA1HardPromptsRubricsParams = z.infer<typeof FNA1HardPromptsRubricsParamsSchema>;

// Autoflation HvM Bot Attempt specific parameters
export const AutoflationHvMParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.AUTOFLATION_HVM_BOT_ATTEMPT),
  DRY_RUN_MODE: z.boolean().default(true),
});

export type AutoflationHvMParams = z.infer<typeof AutoflationHvMParamsSchema>;

// Autoflation MvM Bot Attempt specific parameters
export const AutoflationMvMParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.AUTOFLATION_MVM_BOT_ATTEMPT),
  DRY_RUN_MODE: z.boolean().default(true),
});

export type AutoflationMvMParams = z.infer<typeof AutoflationMvMParamsSchema>;

// Remove Rubric Criteria Bot Attempt specific parameters
export const RemoveRubricCriteriaParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.REMOVE_RUBRIC_CRITERIA_BOT_ATTEMPT),
  DRY_RUN_MODE: z.boolean().default(true),
});

export type RemoveRubricCriteriaParams = z.infer<typeof RemoveRubricCriteriaParamsSchema>;

// Default column mapping for S3 to CDS Upload
export const DEFAULT_S3_TO_CDS_UPLOAD_COLUMN_MAPPING: Record<string, string> = {
  '': '',
};

// S3 to CDS Upload specific parameters
export const S3ToCdsUploadParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.S3_TO_CDS_UPLOAD),
  columnMapping: z.record(z.string(), z.string()).default(DEFAULT_S3_TO_CDS_UPLOAD_COLUMN_MAPPING),
  customerId: z.string().optional(),
  customerIdColumn: z.string().optional(),
  dataType: z.string().default('dockerImage'),
  dataTypeColumn: z.string().optional(),
  referenceType: z.string().default('task'),
  referenceIdColumn: z.string().optional(),
  itemType: z.string().default('response'),
  product: z.string().optional(),
  allowPartialFailures: z.boolean().default(true),
});

export type S3ToCdsUploadParams = z.infer<typeof S3ToCdsUploadParamsSchema>;

// Audio LUFS Normalization specific parameters
export const AudioLufsNormalizationParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.AUDIO_LUFS_NORMALIZATION),
  DRY_RUN_MODE: z.boolean().default(true),
  minLufs: z.number().default(-27),
  maxLufs: z.number().default(-23),
  targetLufs: z.number().default(-25),
  taskIdColumn: z.string().default('task_id'),
});

export type AudioLufsNormalizationParams = z.infer<typeof AudioLufsNormalizationParamsSchema>;

// Remove Background Noise specific parameters
export const RemoveBackgroundNoiseParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.REMOVE_BACKGROUND_NOISE),
  DRY_RUN_MODE: z.boolean().default(true),
  taskIdColumn: z.string().default('task_id'),
  audioMode: z.enum(['denoise', 'remove']).default('denoise'),
});

export type RemoveBackgroundNoiseParams = z.infer<typeof RemoveBackgroundNoiseParamsSchema>;

// Default column mapping for CDS Text Content Upload
export const DEFAULT_CDS_TEXT_CONTENT_UPLOAD_COLUMN_MAPPING: Record<string, string> = {
  '': '',
};

// CDS Text Content Upload specific parameters
export const CdsTextContentUploadParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.CDS_TEXT_CONTENT_UPLOAD),
  contentColumn: z.string().default(''), // Source column containing text content
  outputColumn: z.string().default('cds_url'), // Destination column for CDS URL
  fileType: z.enum(['txt', 'json', 'csv', 'md', 'html', 'xml', 'yaml']).default('txt'),
  customerId: z.string().default(''),
  dataType: z.string().default('text'),
  itemType: z.string().default('response'),
  referenceType: z.string().default('file'),
  filenameColumn: z.string().optional(),
  referenceIdColumn: z.string().optional(),
  allowPartialFailures: z.boolean().default(true),
});

export type CdsTextContentUploadParams = z.infer<typeof CdsTextContentUploadParamsSchema>;

// CDS URL Content Upload specific parameters
export const CdsUrlContentUploadParamsSchema = BaseScriptParamsSchema.extend({
  script: z.literal(CompassTransformScript.CDS_URL_CONTENT_UPLOAD),
  urlColumn: z.string().default(''), // Source column containing URLs to download
  outputColumn: z.string().default('cds_url'), // Destination column for CDS URL
  customerId: z.string().default(''),
  dataType: z.string().optional(), // Auto-detected if not provided
  itemType: z.string().default('response'),
  referenceType: z.string().default('file'),
  filenameColumn: z.string().optional(),
  referenceIdColumn: z.string().optional(),
  overrideContentType: z.string().optional(),
  overrideExtension: z.string().optional(),
  allowPartialFailures: z.boolean().default(true),
});

export type CdsUrlContentUploadParams = z.infer<typeof CdsUrlContentUploadParamsSchema>;

// Union of all script parameter schemas
export const ScriptParamsSchema = z.discriminatedUnion('script', [
  OTSUIPromptGenerationParamsSchema,
  CDSDownloadResponseParamsSchema,
  CDSContentInsertionParamsSchema,
  CDSUrlSigningParamsSchema,
  S3UrlSigningParamsSchema,
  HubstaffScreenshotParamsSchema,
  MultiMangoQATableParamsSchema,
  MultimangoPasswordResetParamsSchema,
  SetCBRoleParamsSchema,
  SyncMultimangoUsersParamsSchema,
  WfeDecryptPasswordsParamsSchema,
  AddHubstaffHoursParamsSchema,
  AgentExecutionParamsSchema,
  FNA1HardPromptsRubricsParamsSchema,
  AutoflationHvMParamsSchema,
  AutoflationMvMParamsSchema,
  RemoveRubricCriteriaParamsSchema,
  S3ToCdsUploadParamsSchema,
  AudioLufsNormalizationParamsSchema,
  RemoveBackgroundNoiseParamsSchema,
  CdsTextContentUploadParamsSchema,
  CdsUrlContentUploadParamsSchema,
  // Add other script parameter schemas here as needed
]);

export type ScriptParams = z.infer<typeof ScriptParamsSchema>;

// Helper function to get default values from a Zod schema
const getZodDefaults = <T extends z.ZodType>(
  schema: T,
  scriptType: CompassTransformScript,
): z.infer<T> => {
  try {
    // Include the required script field to satisfy the schema
    const result: z.infer<T> = schema.parse({ script: scriptType });
    return result;
  } catch (error) {
    const defaultResult: z.infer<T> = { script: scriptType };
    return defaultResult;
  }
};

// Map of script types to their corresponding schema
const SCRIPT_TO_SCHEMA_MAP: Partial<Record<CompassTransformScript, z.ZodType>> = {
  [CompassTransformScript.OTS_UI_PROMPT_GENERATION]: OTSUIPromptGenerationParamsSchema,
  [CompassTransformScript.CDS_DOWNLOAD_RESPONSE]: CDSDownloadResponseParamsSchema,
  [CompassTransformScript.CDS_CONTENT_INSERTION]: CDSContentInsertionParamsSchema,
  [CompassTransformScript.CDS_URL_SIGNING]: CDSUrlSigningParamsSchema,
  [CompassTransformScript.S3_URL_SIGNING]: S3UrlSigningParamsSchema,
  [CompassTransformScript.HUBSTAFF_SCREENSHOT]: HubstaffScreenshotParamsSchema,
  [CompassTransformScript.MULTIMANGO_QA_TABLE]: MultiMangoQATableParamsSchema,
  [CompassTransformScript.MULTIMANGO_USER_PASSWORD_RESET]: MultimangoPasswordResetParamsSchema,
  [CompassTransformScript.SET_CB_ROLE]: SetCBRoleParamsSchema,
  [CompassTransformScript.SYNC_MULTIMANGO_USERS]: SyncMultimangoUsersParamsSchema,
  [CompassTransformScript.WFE_DECRYPT_PASSWORDS]: WfeDecryptPasswordsParamsSchema,
  [CompassTransformScript.ADD_HUBSTAFF_HOURS]: AddHubstaffHoursParamsSchema,
  [CompassTransformScript.AGENT_EXECUTION]: AgentExecutionParamsSchema,
  [CompassTransformScript.FNA1_HARD_PROMPTS_RUBRICS_BOT_ATTEMPT]:
    FNA1HardPromptsRubricsParamsSchema,
  [CompassTransformScript.AUTOFLATION_HVM_BOT_ATTEMPT]: AutoflationHvMParamsSchema,
  [CompassTransformScript.AUTOFLATION_MVM_BOT_ATTEMPT]: AutoflationMvMParamsSchema,
  [CompassTransformScript.REMOVE_RUBRIC_CRITERIA_BOT_ATTEMPT]: RemoveRubricCriteriaParamsSchema,
  [CompassTransformScript.S3_TO_CDS_UPLOAD]: S3ToCdsUploadParamsSchema,
  [CompassTransformScript.AUDIO_LUFS_NORMALIZATION]: AudioLufsNormalizationParamsSchema,
  [CompassTransformScript.REMOVE_BACKGROUND_NOISE]: RemoveBackgroundNoiseParamsSchema,
  [CompassTransformScript.CDS_TEXT_CONTENT_UPLOAD]: CdsTextContentUploadParamsSchema,
  [CompassTransformScript.CDS_URL_CONTENT_UPLOAD]: CdsUrlContentUploadParamsSchema,
  // Add more script-to-schema mappings as needed
};

// Helper function to get default parameters for a specific script
export const getDefaultScriptParams = (script: CompassTransformScript): ScriptParams => {
  const schema = SCRIPT_TO_SCHEMA_MAP[script];
  if (schema) {
    return getZodDefaults(schema, script);
  }
  // Use type assertion to tell TypeScript this is valid
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return { script } as ScriptParams;
};
